import {Command, Flags} from '@oclif/core';
import {outputWarn} from '@shopify/cli-kit/node/output';
import {normalizePath} from '@shopify/cli-kit/node/path';
import {readAndParseDotEnv} from '@shopify/cli-kit/node/dot-env';

import {getBugsnag, initializeBugsnag} from '../../utils/bugsnag.js';
import {createDeploy} from '../../deploy/index.js';
import {
  deployDefaults,
  getBuildCommandFromLockFile,
  parseToken,
  verifyConfig,
} from '../../utils/utils.js';
import type {
  DeploymentConfig,
  EnvironmentVariable,
} from '../../deploy/types.js';

export class Deploy extends Command {
  static description = 'Creates a deployment to Oxygen';
  static hidden = false;
  static flags = {
    assetsFolder: Flags.string({
      char: 'a',
      description: 'Assets folder',
      default: String(deployDefaults.assetsDirDefault),
      required: false,
    }),
    buildCommand: Flags.string({
      char: 'b',
      description: 'Build command',
      required: false,
      default: String(deployDefaults.buildCommandDefault!),
      parse: (input): Promise<string> => {
        this.hasCustomBuildCommand = true;
        return Promise.resolve(input);
      },
    }),
    disableBugsnag: Flags.boolean({
      description: 'Disable Bugsnag error reporting',
      required: false,
      default: false,
    }),
    environmentTag: Flags.string({
      char: 'e',
      description: 'Tag of the environment to deploy to',
      required: false,
    }),
    path: Flags.string({
      char: 'p',
      description: 'Root path',
      default: './',
      required: false,
    }),
    defaultEnvironment: Flags.boolean({
      env: 'OXYGEN_DEFAULT_ENVIRONMENT',
      description: 'Deploys to the default environment of the app',
      required: false,
      default: false,
    }),
    skipBuild: Flags.boolean({
      char: 's',
      description: 'Skip running build command',
      required: false,
      default: false,
    }),
    token: Flags.string({
      char: 't',
      description: 'Oxygen deployment token',
      env: 'OXYGEN_DEPLOYMENT_TOKEN',
      required: true,
    }),
    workerFolder: Flags.string({
      char: 'w',
      description: 'Worker folder',
      default: String(deployDefaults.workerDirDefault),
      required: false,
    }),
    workerOnly: Flags.boolean({
      char: 'o',
      description: 'Worker only deployment',
      default: false,
      required: false,
    }),
    metadataDescription: Flags.string({
      description:
        'Description of the deployment. Will be saved and displayed in the Shopify admin',
      required: false,
      env: 'OXYGEN_METADATA_DESCRIPTION',
    }),
    metadataHydrogenVersion: Flags.string({
      description: 'The Hydrogen version of the deployment from package.json',
      required: false,
      env: 'OXYGEN_METADATA_HYDROGEN_VERSION',
    }),
    metadataUrl: Flags.string({
      description:
        'URL that links to the deployment. Will be saved and displayed in the Shopify admin',
      required: false,
      env: 'OXYGEN_METADATA_URL',
    }),
    metadataUser: Flags.string({
      description:
        'User that initiated the deployment. Will be saved and displayed in the Shopify admin',
      required: false,
      env: 'OXYGEN_METADATA_USER',
    }),
    metadataVersion: Flags.string({
      description:
        'A version identifier for the deployment. Will be saved and displayed in the Shopify admin',
      required: false,
      env: 'OXYGEN_METADATA_VERSION',
    }),
    generateAuthBypassToken: Flags.boolean({
      description:
        'Generate an auth bypass token used to perform end-to-end testing against the deployment.',
      required: false,
      default: false,
    }),
    authBypassTokenDuration: Flags.string({
      description:
        'Specify the duration (in hours) up to 12 hours for the authentication bypass token. Defaults to `2`.',
      required: false,
      dependsOn: ['generateAuthBypassToken'],
    }),
    environmentFile: Flags.string({
      description:
        'Path to an environment file to override existing environment variables for the deployment.',
      required: false,
    }),
  };

  static hasCustomBuildCommand = false;

  async run() {
    try {
      const {flags} = await this.parse(Deploy);
      const rootPath = normalizePath(flags.path);

      let overriddenEnvironmentVariables: EnvironmentVariable[] | undefined;

      if (flags.environmentFile) {
        const file = await readAndParseDotEnv(flags.environmentFile);

        overriddenEnvironmentVariables = Object.entries(file.variables).map(
          ([key, value]) => ({
            isSecret: true,
            key,
            value,
          }),
        );
      }

      initializeBugsnag(flags.disableBugsnag);
      const Bugsnag = getBugsnag();

      const deploymentUrl =
        // eslint-disable-next-line no-process-env
        process.env.UNSAFE_OXYGEN_DEPLOYMENT_URL ||
        'https://oxygen.shopifyapps.com';
      Bugsnag?.addMetadata('flags', {
        flags: {...flags, token: '***'},
        deploymentUrl,
      });

      const config: DeploymentConfig = {
        assetsDir: normalizePath(flags.assetsFolder),
        bugsnag: !flags.disableBugsnag,
        buildCommand: flags.buildCommand!,
        deploymentToken: parseToken(flags.token!),
        environmentTag: flags.environmentTag,
        deploymentUrl,
        metadata: {
          description: flags.metadataDescription,
          hydrogenVersion: flags.metadataHydrogenVersion,
          url: flags.metadataUrl,
          user: flags.metadataUser,
          version: flags.metadataVersion,
        },
        defaultEnvironment: flags.defaultEnvironment,
        rootPath,
        skipBuild: flags.skipBuild,
        workerDir: normalizePath(flags.workerFolder),
        workerOnly: flags.workerOnly,
        generateAuthBypassToken: flags.generateAuthBypassToken,
        authBypassTokenDuration: flags.authBypassTokenDuration,
        overriddenEnvironmentVariables,
      };

      await verifyConfig({config});
      if (!Deploy.hasCustomBuildCommand && !config.skipBuild) {
        config.buildCommand = getBuildCommandFromLockFile(config);
      }
      await createDeploy({config});
    } catch (error) {
      const Bugsnag = getBugsnag();
      if (Bugsnag && (error instanceof Error || typeof error === 'string')) {
        Bugsnag.notify(error);
      }

      if (error instanceof Error) {
        outputWarn(error.message);
      } else {
        outputWarn(error as string);
      }

      this.exit(1);
    }
  }
}
